import * as XLSX from 'xlsx';
import type { Customization } from '../types/customization';
import type { Dish } from '../types/dish';
import type { Recipe, RecipeIngredient } from '../types/recipe';
import type { WarehouseGoodsItem } from '../types/warehouseGoodsCatalog';
import { dishesToMenuRefs, findDishForRecipe, normalizeCodeKey } from './recipeCukcukSync';
import { mergeCukcukDraftMaterials } from './cukcukDraftMaterialMappings';

const MENU_COLUMN_COUNT = 36;
const EXAMPLE_COLUMN_COUNT = 39;
const stripDiacritics = new RegExp('[\\u0300-\\u036f]', 'g');

// CUKCUK validates the menu workbook as a legacy workbook, but it also
// expects the same grouped header layout as the downloaded template.  Keep
// these ranges in the generated sheet so the import file is structurally
// equivalent to the official template instead of being a plain data grid.
const CUKCUK_MENU_MERGES: XLSX.Range[] = [
  { s: { c: 9, r: 10 }, e: { c: 9, r: 11 } },
  { s: { c: 10, r: 10 }, e: { c: 10, r: 11 } },
  { s: { c: 11, r: 10 }, e: { c: 11, r: 11 } },
  { s: { c: 0, r: 9 }, e: { c: 17, r: 9 } },
  { s: { c: 18, r: 9 }, e: { c: 21, r: 9 } },
  { s: { c: 22, r: 9 }, e: { c: 23, r: 9 } },
  { s: { c: 12, r: 10 }, e: { c: 12, r: 11 } },
  { s: { c: 13, r: 10 }, e: { c: 13, r: 11 } },
  { s: { c: 14, r: 10 }, e: { c: 14, r: 11 } },
  { s: { c: 15, r: 10 }, e: { c: 15, r: 11 } },
  { s: { c: 0, r: 10 }, e: { c: 0, r: 11 } },
  { s: { c: 1, r: 10 }, e: { c: 1, r: 11 } },
  { s: { c: 3, r: 10 }, e: { c: 3, r: 11 } },
  { s: { c: 5, r: 10 }, e: { c: 5, r: 11 } },
  { s: { c: 6, r: 10 }, e: { c: 6, r: 11 } },
  { s: { c: 7, r: 10 }, e: { c: 8, r: 10 } },
  { s: { c: 2, r: 10 }, e: { c: 2, r: 11 } },
  { s: { c: 4, r: 10 }, e: { c: 4, r: 11 } },
  { s: { c: 19, r: 10 }, e: { c: 19, r: 11 } },
  { s: { c: 20, r: 10 }, e: { c: 20, r: 11 } },
  { s: { c: 21, r: 10 }, e: { c: 21, r: 11 } },
  { s: { c: 29, r: 9 }, e: { c: 30, r: 9 } },
  { s: { c: 24, r: 9 }, e: { c: 25, r: 9 } },
  { s: { c: 26, r: 9 }, e: { c: 26, r: 11 } },
  { s: { c: 27, r: 9 }, e: { c: 28, r: 9 } },
  { s: { c: 29, r: 10 }, e: { c: 29, r: 11 } },
  { s: { c: 16, r: 10 }, e: { c: 16, r: 11 } },
  { s: { c: 30, r: 10 }, e: { c: 30, r: 11 } },
  { s: { c: 22, r: 10 }, e: { c: 22, r: 11 } },
  { s: { c: 23, r: 10 }, e: { c: 23, r: 11 } },
  { s: { c: 24, r: 10 }, e: { c: 24, r: 11 } },
  { s: { c: 25, r: 10 }, e: { c: 25, r: 11 } },
  { s: { c: 27, r: 10 }, e: { c: 27, r: 11 } },
  { s: { c: 28, r: 10 }, e: { c: 28, r: 11 } },
  { s: { c: 17, r: 10 }, e: { c: 17, r: 11 } },
  { s: { c: 18, r: 10 }, e: { c: 18, r: 11 } },
  { s: { c: 31, r: 9 }, e: { c: 35, r: 9 } },
  { s: { c: 31, r: 10 }, e: { c: 31, r: 11 } },
  { s: { c: 32, r: 10 }, e: { c: 32, r: 11 } },
  { s: { c: 33, r: 10 }, e: { c: 33, r: 11 } },
  { s: { c: 34, r: 10 }, e: { c: 34, r: 11 } },
  { s: { c: 35, r: 10 }, e: { c: 35, r: 11 } },
];

export type CukcukRecipeExportWarningType =
  | 'missing_recipe'
  | 'unmatched_recipe'
  | 'duplicate_recipe_target'
  | 'missing_material_mapping'
  | 'invalid_quantity'
  | 'hot_iced_difference'
  | 'option_recipe_not_exported'
  | 'unsupported_recipe_type';

export interface CukcukRecipeExportWarning {
  type: CukcukRecipeExportWarningType;
  itemCode?: string;
  itemName?: string;
  recipeId?: string;
  ingredientName?: string;
  message: string;
}

export interface CukcukRecipeExportSummary {
  exportedDishCount: number;
  matchedRecipeCount: number;
  exportedIngredientRows: number;
  warnings: CukcukRecipeExportWarning[];
}

export interface BuildCukcukRecipeExportInput {
  dishes: Dish[];
  recipes: Recipe[];
  materials: WarehouseGoodsItem[];
  customizations?: Customization[];
  /** Include CUKCUK service-preference rows only when explicitly requested. */
  includeServicePreferences?: boolean;
  /**
   * Opt-in only: include the companion draft material codes for the six
   * semi-finished ingredients absent from the official CUKCUK master.
   * Callers must import that companion master workbook first.
   */
  includeDraftMaterialMappings?: boolean;
}

export interface BuildCukcukRecipeExportResult {
  workbook: XLSX.WorkBook;
  summary: CukcukRecipeExportSummary;
}

/**
 * Inject generated menu rows into the workbook downloaded from CUKCUK.
 *
 * CUKCUK's import endpoint validates more than the visible cell values: the
 * legacy BIFF8 workbook must retain its example sheet, merged headers,
 * column metadata, and other template markers.  Starting with the official
 * workbook and replacing only rows 13 onward keeps those markers intact.
 */
export const applyCukcukRecipeTemplate = (
  templateBytes: ArrayBuffer | Uint8Array,
  generatedWorkbook: XLSX.WorkBook,
): XLSX.WorkBook => {
  const templateWorkbook = XLSX.read(templateBytes, {
    cellStyles: true,
    cellNF: true,
  });
  const templateMenu = templateWorkbook.Sheets['Thực đơn'];
  const generatedMenu = generatedWorkbook.Sheets['Thực đơn'];

  if (!templateMenu || !generatedMenu) {
    throw new Error('CUKCUK template must contain a Thực đơn sheet.');
  }

  // Excel rows are zero-based in SheetJS; row 12 is the first data row (row
  // 13 as displayed in Excel).  Leave the title/header/instruction rows from
  // the official template untouched.
  Object.keys(templateMenu).forEach((address) => {
    if (address.startsWith('!')) return;
    const cell = XLSX.utils.decode_cell(address);
    if (cell.r >= 12) delete templateMenu[address];
  });

  Object.keys(generatedMenu).forEach((address) => {
    if (address.startsWith('!')) return;
    const cell = XLSX.utils.decode_cell(address);
    if (cell.r < 12) return;
    templateMenu[address] = { ...generatedMenu[address] };
  });

  // Keep all layout metadata (merges, hidden columns, widths and row styles)
  // from the official template.  Only the used range changes with the number
  // of exported menu rows.
  if (generatedMenu['!ref']) templateMenu['!ref'] = generatedMenu['!ref'];
  return templateWorkbook;
};

/** Warnings that can make a recipe row unusable in the CUKCUK import. */
export const CUKCUK_RECIPE_BLOCKING_WARNING_TYPES: ReadonlySet<CukcukRecipeExportWarningType> = new Set<CukcukRecipeExportWarningType>([
  'missing_recipe',
  'missing_material_mapping',
  'invalid_quantity',
  'duplicate_recipe_target',
]);

export const isBlockingCukcukRecipeWarning = (
  warning: CukcukRecipeExportWarning,
): boolean => CUKCUK_RECIPE_BLOCKING_WARNING_TYPES.has(warning.type);

const isCukcukRecipeCapableDish = (dish: Dish): boolean => {
  const typeKeys = [dish.type, dish.typeName]
    .filter(Boolean)
    .map((value) => normalizeName(value));
  return typeKeys.includes('mon an') || typeKeys.includes('do uong pha che');
};

const normalizeName = (value: unknown): string =>
  String(value ?? '')
    .normalize('NFD')
    .replace(stripDiacritics, '')
    .replace(/đ/gi, 'd')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

// Recipe workbooks use a few shorthand names while CUKCUK keeps the full
// catalog name. These aliases are conservative: each one points to a single
// existing raw-material name with the same meaning, so we never guess a
// supplier or invent a CUKCUK code.
const MATERIAL_NAME_ALIASES: Record<string, string> = {
  'ruou bailey': 'baileys',
  'bot cacao': 'cacao',
  'milo': 'bot milo',
  'dao mieng': 'dao hop',
  'ethiopia sidamo': 'hat ethiopia sidamo twakok g1',
  'ethiopia guji whiskey cream': 'hat ethiopia guji whiskey cream',
  'kenya aa': 'kenya',
  'banh chocolate': 'banh socola',
  'cartimo lac duong': 'catimor',
  'dau tam': 'mut dau tam',
  'vun dua': 'dua kho',
  'nuoc': 'nuoc loc',
  'da': 'da vien',
  'da tron': 'da vien',
  'luc tra': 'tra lai',
};

const hasValue = (value: unknown): boolean => String(value ?? '').trim() !== '';

interface ParsedQuantity {
  value: number;
  unitHint?: string;
}

const parseQuantity = (value: unknown): ParsedQuantity | null => {
  if (!hasValue(value)) return null;
  const text = String(value).trim().replace(',', '.');
  const match = text.match(/^([+-]?\d+(?:\.\d+)?)\s*(kg|g|ml|l|quả|lon|hộp|chai|gói|cái)?$/i);
  if (!match) return null;
  const parsed = Number(match[1]);
  if (!Number.isFinite(parsed)) return null;
  return { value: parsed, unitHint: match[2]?.trim().toLowerCase() };
};

const isIcedDish = (dish: Dish): boolean => /(đá|ice|iced|lạnh)/i.test(dish.name);
const isHotDish = (dish: Dish): boolean => /(nóng|hot)/i.test(dish.name);

const pickRecipeAmount = (
  ingredient: RecipeIngredient,
  dish: Dish,
  warnings: CukcukRecipeExportWarning[],
): ParsedQuantity | null => {
  const hot = parseQuantity(ingredient.hotAmount);
  const iced = parseQuantity(ingredient.icedAmount);

  if (isIcedDish(dish)) return iced ?? hot;
  if (isHotDish(dish)) return hot ?? iced;

  if (hot !== null && iced !== null && hot.value !== iced.value) {
    // A large part of the 8am menu stores cold-only recipes as hot=0 and
    // iced>0 without putting “đá/iced” in the item name (for example COLD
    // BREW TONIC). Prefer the usable iced quantity instead of exporting a
    // zero line into CUKCUK. Hot-named dishes were handled above.
    if (hot.value === 0 && iced.value !== 0) return iced;
    warnings.push({
      type: 'hot_iced_difference',
      itemCode: dish.code,
      itemName: dish.name,
      ingredientName: ingredient.name,
      message: `Món ${dish.name} có định lượng nóng (${hot.value}) khác đá (${iced.value}); file dùng định lượng nóng.`,
    });
  }

  return hot ?? iced;
};

const findMaterial = (
  ingredientName: string,
  materials: WarehouseGoodsItem[],
  preferredCode?: string,
  preferredUnit?: string,
): WarehouseGoodsItem | undefined => {
  const key = normalizeName(ingredientName);
  if (!key) return undefined;

  // Packaged CUKCUK items often have a recipe ingredient shortened to a
  // family name (for example "Ethiopia G1"), while the material catalog has
  // multiple package sizes. When the dish itself is also a catalog code,
  // prefer that exact code to avoid an ambiguous substring match.
  const preferred = normalizeName(preferredCode);
  if (preferred) {
    const byDishCode = materials.find((material) =>
      [material.productCode, material.cukcukMaterialCode]
        .some((value) => normalizeName(value) === preferred),
    );
    if (byDishCode) return byDishCode;
  }

  const aliasKey = MATERIAL_NAME_ALIASES[key] || key;

  const matchesName = (material: WarehouseGoodsItem): boolean =>
    [material.name, material.productCode, material.cukcukMaterialCode]
      .some((value) => normalizeName(value) === aliasKey);
  const unitKey = normalizeName(preferredUnit);
  const exactCandidates = materials.filter(matchesName);
  if (unitKey) {
    const exactUnitMatch = exactCandidates.find((material) => normalizeName(material.cukcukUnit) === unitKey);
    if (exactUnitMatch) return exactUnitMatch;
  }
  const candidates = materials.filter((material) => {
    const materialKey = normalizeName(material.name);
    return materialKey && (materialKey.includes(aliasKey) || aliasKey.includes(materialKey));
  });
  if (unitKey) {
    const unitMatches = candidates.filter((material) => normalizeName(material.cukcukUnit) === unitKey);
    if (unitMatches.length === 1) return unitMatches[0];
  }
  if (exactCandidates[0]) return exactCandidates[0];
  return candidates.length === 1 ? candidates[0] : undefined;
};

const optionRowsForDish = (dishCode: string, customizations: Customization[]) => {
  const seen = new Set<string>();
  return customizations
    .filter((customization) => customization.dishCode?.some((code) => code.trim() === dishCode.trim()))
    .flatMap((customization) => customization.groups || [])
    .flatMap((group) => group.dishCodes || [])
    .filter((option) => option.isActive !== false && hasValue(option.name))
    .filter((option) => {
      const key = `${option.code}|${option.name}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((option) => ({ label: option.name.trim(), extraPrice: option.price ?? 0 }));
};

const sectionRow = (): unknown[] => {
  const row = Array(MENU_COLUMN_COUNT).fill('');
  row[0] = 'Thông tin món ăn/đồ uống';
  row[18] = 'Định lượng nguyên vật liệu (NVL)';
  row[22] = 'Sở thích phục vụ';
  row[24] = 'Cách chế biến món ăn theo NVL';
  row[26] = 'Các món trong món ăn, đồ uống theo nhóm (Mã món)';
  row[27] = 'Các món trong combo';
  row[29] = 'Các món trong set mặc định';
  row[31] = 'Đơn vị tính chuyển đổi';
  return row;
};

const headerRow = (): unknown[] => [
  'Loại (*)', 'Mã món (*)', 'Mã vạch', 'Tên món (*)', 'Tên món ngôn ngữ khác',
  'Đơn vị tính (*)', 'Giá bán (*)', 'Phí dịch vụ đặc biệt', '', 'Giá vốn',
  'Thay đổi theo thời giá', 'Điều chỉnh giá tự do', 'Áp dụng giá theo khu vực',
  'Mô tả', 'Nhóm thực đơn', 'Thứ tự món', 'Là món đặc trưng', 'Hiển thị trên thực đơn',
  'Mã NVL', 'Tên NVL', 'Đơn vị tính', 'Số lượng', 'Sở thích phục vụ', 'Thu thêm',
  'Tên món', 'Đơn vị tính', '', 'Mã món', 'Số lượng', 'Mã món', 'Số lượng',
  'Đơn vị tính  ', 'Tỷ lệ chuyển đổi', 'Phép tính', 'Giá bán', 'Đơn vị bán mặc định',
];

const subHeaderRow = (): unknown[] => {
  const row = Array(MENU_COLUMN_COUNT).fill('');
  row[7] = 'Số tiền';
  row[8] = 'Phần trăm (%)';
  return row;
};

const instructionsRows = (): unknown[][] => [
  ['MS-9000'],
  (() => { const row = Array(MENU_COLUMN_COUNT).fill(''); row[0] = 'Đối với các món ăn, đồ uống pha chế có Định lượng nguyên vật liệu chế biến, khai báo Định lượng nguyên vật liệu bằng cách unhide các cột từ cột Q đến cột T'; row[14] = 'Xem hướng dẫn khai báo định lượng'; return row; })(),
  (() => { const row = Array(MENU_COLUMN_COUNT).fill(''); row[0] = 'Đối với các món ăn có các Sở thích phục vụ về cách chế biến, khai báo các sở thích bằng cách unhide các cột từ cột U đến cột V'; row[14] = 'Xem hướng dẫn khai báo sở thích phục vụ'; return row; })(),
  (() => { const row = Array(MENU_COLUMN_COUNT).fill(''); row[0] = 'Đối với món ăn theo nguyên vật liệu; combo; món ăn, đồ uống theo nhóm, set mặc định, khai báo các món chi tiết bằng cách unhide các cột từ cột Y đến cột AC'; row[14] = 'Xem hướng dẫn khai báo món ăn theo nguyên vật liệu'; return row; })(),
  (() => { const row = Array(MENU_COLUMN_COUNT).fill(''); row[0] = 'Đối với các món ăn có Đơn vị tính chuyển đổi, khai báo Đơn vị tính chuyển đổi bằng cách unhide các cột từ AE đến AI'; row[14] = 'Xem hướng dẫn khai báo món ăn theo nhóm'; return row; })(),
  (() => { const row = Array(MENU_COLUMN_COUNT).fill(''); row[14] = 'Xem hướng dẫn khai báo combo'; return row; })(),
  (() => { const row = Array(MENU_COLUMN_COUNT).fill(''); row[14] = 'Xem hướng dẫn khai báo set mặc định'; return row; })(),
  (() => { const row = Array(MENU_COLUMN_COUNT).fill(''); row[0] = 'Lưu ý: Để unhide các cột từ R đến AI, vui lòng: bôi đen 2 cột Q và AJ, bấm chuột phải, chọn Unhide'; return row; })(),
  (() => { const row = Array(MENU_COLUMN_COUNT).fill(''); row[0] = 'Xem các ví dụ hướng dẫn'; row[18] = 'Khi khai báo các thông tin Định lượng NVL, Sở thích phục vụ, Cách chế biến món ăn theo NVL, Các món trong món ăn đồ uống theo nhóm, Các món trong combo và Set mặc định thì không cần lặp lại các thông tin của món từ cột A đến cột K'; return row; })(),
];

const baseDishRow = (dish: Dish): unknown[] => {
  const row = Array(MENU_COLUMN_COUNT).fill('');
  row[0] = dish.type || dish.typeName || 'Đồ uống pha chế';
  row[1] = dish.code;
  row[2] = dish.barcode || '';
  row[3] = dish.name;
  row[5] = dish.unit || 'Phần';
  row[6] = Number.isFinite(dish.price) ? dish.price : 0;
  row[9] = 0;
  row[13] = dish.description || '';
  row[14] = dish.groupName || dish.group || '';
  row[15] = dish.displayOrder ?? '';
  row[16] = '';
  row[17] = dish.isActive === false ? 'Không' : 'Có';
  return row;
};

const makeSheet = (rows: unknown[][]): XLSX.WorkSheet => {
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet['!cols'] = Array.from({ length: MENU_COLUMN_COUNT }, (_, index) => ({
    // Match the official template: M and S:AJ are hidden by default.  In
    // particular, column R (the menu visibility flag) must stay visible.
    hidden: index === 12 || (index >= 18 && index <= 35),
  }));
  sheet['!merges'] = CUKCUK_MENU_MERGES.map((merge) => ({
    s: { ...merge.s },
    e: { ...merge.e },
  }));
  return sheet;
};

const buildExampleSheet = (): XLSX.WorkSheet => {
  const section = Array(EXAMPLE_COLUMN_COUNT).fill('');
  section[0] = 'Thông tin món ăn/đồ uống';
  section[18] = 'Định lượng nguyên vật liệu (NVL)';
  section[22] = 'Sở thích phục vụ';
  section[24] = 'Cách chế biến món ăn theo NVL';
  section[26] = 'Các món trong món ăn, đồ uống theo nhóm (Mã món)';
  section[27] = 'Các món trong combo';
  section[29] = 'Các món trong set mặc định';
  section[31] = 'Chính sách giá bán theo khu vực';
  section[34] = 'Đơn vị tính chuyển đổi';
  const header = [
    'Loại (*)', 'Mã món (*)', 'Mã vạch', 'Tên món (*)', 'Tên món ngôn ngữ khác', 'Đơn vị tính (*)', 'Giá bán (*)', 'Giá vốn', 'Phí dịch vụ đặc biệt', '', 'Thay đổi theo thời giá', 'Điều chỉnh giá tự do', 'Áp dụng giá theo khu vực', 'Mô tả', 'Nhóm thực đơn', 'Thứ tự món', 'Là món đặc trưng', 'Hiển thị trên thực đơn', 'Mã NVL', 'Tên NVL', 'Đơn vị tính', 'Số lượng', 'Sở thích phục vụ', 'Thu thêm', 'Tên món', 'Đơn vị tính', '', 'Mã món', 'Số lượng', 'Mã món', 'Số lượng', 'Khu vực', 'Giá bán', 'Sử dụng', 'Đơn vị tính  ', 'Tỷ lệ chuyển đổi', 'Phép tính', 'Giá bán', 'Đơn vị bán mặc định',
  ];
  const subHeader = Array(EXAMPLE_COLUMN_COUNT).fill('');
  subHeader[8] = 'Số tiền';
  subHeader[9] = 'Số phần trăm (%)';
  const example = Array(EXAMPLE_COLUMN_COUNT).fill('');
  ['Món ăn', 'VÍ_DỤ', '', 'Món ví dụ', '', 'Phần', 0, 0, '', '', '', '', '', '', 'Ví dụ', '', '', '', 'AM01', 'Baileys', 'Chai', 10].forEach((value, index) => { example[index] = value; });
  const sheet = XLSX.utils.aoa_to_sheet([
    ['1. Muốn nhập một món ăn thông thường, có công thức định lượng nguyên vật liệu chế biến, vui lòng xem ví dụ dưới đây:'],
    [],
    section,
    header,
    subHeader,
    example,
  ]);
  // The official example sheet keeps all columns visible; only the menu
  // sheet uses hidden columns for its optional sections.
  sheet['!cols'] = Array.from({ length: EXAMPLE_COLUMN_COUNT }, () => ({}));
  return sheet;
};

export const buildCukcukRecipeExportWorkbook = (
  input: BuildCukcukRecipeExportInput,
): BuildCukcukRecipeExportResult => {
  const warnings: CukcukRecipeExportWarning[] = [];
  const materials = input.includeDraftMaterialMappings
    ? mergeCukcukDraftMaterials(input.materials)
    : input.materials;
  const dishes = input.dishes.filter((dish) => !!dish.code?.trim() && !!dish.name?.trim() && !dish.isVirtualDish);
  const menuRefs = dishesToMenuRefs(dishes);
  const recipeByDish = new Map<string, Recipe>();
  const matchedRecipeIds = new Set<string>();

  input.recipes.filter((recipe) => (recipe.ingredients || []).length > 0).forEach((recipe) => {
    const match = findDishForRecipe(recipe, menuRefs, { minScore: 680, minGap: 30 });
    if (!match) {
      warnings.push({
        type: 'unmatched_recipe',
        recipeId: recipe.id,
        itemCode: recipe.itemId,
        itemName: recipe.itemName,
        message: `Không tìm thấy món CUKCUK phù hợp cho công thức ${recipe.itemName}.`,
      });
      return;
    }

    // Do not force a new, code-specific recipe onto a shorter legacy dish
    // merely because the names contain one another (A44 "...socola" must not
    // become A28 "Bánh sừng bò" when A44 is not in the current CUKCUK menu).
    // It remains visible as an informational unmatched recipe instead.
    const recipeCodeKey = normalizeCodeKey(recipe.itemId);
    const matchedDishCodeKey = normalizeCodeKey(match.dish.code);
    const looksLikeDistinctNumericCode =
      /^a\d+$/.test(recipeCodeKey) &&
      /^a\d+$/.test(matchedDishCodeKey) &&
      recipeCodeKey !== matchedDishCodeKey &&
      match.matchBy === 'name_contains';
    if (looksLikeDistinctNumericCode) {
      warnings.push({
        type: 'unmatched_recipe',
        recipeId: recipe.id,
        itemCode: recipe.itemId,
        itemName: recipe.itemName,
        message: `Không tìm thấy món CUKCUK phù hợp cho công thức ${recipe.itemName}; mã ${recipe.itemId} chưa có trên menu hiện tại.`,
      });
      return;
    }
    const existing = recipeByDish.get(match.dish.code);
    if (existing) {
      warnings.push({
        type: 'duplicate_recipe_target',
        recipeId: recipe.id,
        itemCode: match.dish.code,
        itemName: match.dish.name,
        message: `Có nhiều công thức cùng ghép vào món ${match.dish.code}; ưu tiên công thức có tên khớp chính xác với món CUKCUK.`,
      });

      // A legacy duplicate can share an item code while only one recipe has
      // the correct dish name (for example A28: regular croissant vs.
      // chocolate croissant). Prefer the exact name match so the exported
      // ingredient row is deterministic and not dependent on Firestore order.
      const dishName = normalizeName(match.dish.name);
      const existingExact = normalizeName(existing.itemName) === dishName;
      const candidateExact = normalizeName(recipe.itemName) === dishName;
      if (candidateExact && !existingExact) {
        recipeByDish.set(match.dish.code, recipe);
        matchedRecipeIds.delete(existing.id || '');
        if (recipe.id) matchedRecipeIds.add(recipe.id);
      }
      return;
    }
    recipeByDish.set(match.dish.code, recipe);
    if (recipe.id) matchedRecipeIds.add(recipe.id);
  });

  // A single 8am recipe may intentionally cover several CUKCUK codes (for
  // example LATTE -> A50/A8 or AMERICANO -> A66/A6). Register those aliases
  // against the actual menu rows so each CUKCUK variant receives the same
  // formula instead of being reported as a missing recipe.
  input.recipes.forEach((recipe) => {
    if (!recipe.itemIdAliases?.length) return;
    const canonicalDish = menuRefs.find(
      (dish) => normalizeName(dish.code) === normalizeName(recipe.itemId),
    );
    const canonicalRecipe = canonicalDish ? recipeByDish.get(canonicalDish.code) : undefined;
    if (!canonicalRecipe) return;
    recipe.itemIdAliases.forEach((alias) => {
      const aliasDish = menuRefs.find(
        (dish) => normalizeName(dish.code) === normalizeName(alias),
      );
      if (aliasDish && !recipeByDish.has(aliasDish.code)) {
        recipeByDish.set(aliasDish.code, canonicalRecipe);
      }
    });
  });

  const dataRows: unknown[][] = [];
  let exportedIngredientRows = 0;

  dishes.forEach((dish) => {
    // CUKCUK only accepts định lượng NVL for món ăn and đồ uống pha chế.
    // Mặt hàng khác and đồ uống đóng chai are catalog/sale items; adding
    // ingredient rows for them makes CUKCUK reject those rows with
    // "Không nhập định lượng NVL cho mặt hàng ...".
    if (!isCukcukRecipeCapableDish(dish)) {
      if (recipeByDish.has(dish.code)) {
        warnings.push({
          type: 'unsupported_recipe_type',
          itemCode: dish.code,
          itemName: dish.name,
          message: `Loại món ${dish.type || dish.typeName || 'không xác định'} của ${dish.name} không nhận định lượng NVL trong CUKCUK; đã bỏ qua.`,
        });
      }
      return;
    }
    const recipe = recipeByDish.get(dish.code);
    const relevantType = dish.type === 'Món ăn' || dish.type === 'Đồ uống pha chế' || dish.typeName === 'Món ăn' || dish.typeName === 'Đồ uống pha chế';
    if (!recipe && relevantType) {
      warnings.push({
        type: 'missing_recipe',
        itemCode: dish.code,
        itemName: dish.name,
        message: `Món ${dish.code} - ${dish.name} chưa có công thức 8am được ghép.`,
      });
    }

    const ingredientCandidates = (recipe?.ingredients || []).flatMap((ingredient) => {
      const quantity = pickRecipeAmount(ingredient, dish, warnings);
      const material = findMaterial(ingredient.name, materials, dish.code, quantity?.unitHint);
      if (!material?.cukcukMaterialCode || !material.cukcukUnit) {
        warnings.push({
          type: 'missing_material_mapping',
          itemCode: dish.code,
          itemName: dish.name,
          ingredientName: ingredient.name,
          message: `Nguyên liệu ${ingredient.name} chưa có mã hoặc đơn vị CUKCUK.`,
        });
        return [];
      }

      if (quantity === null) {
        warnings.push({
          type: 'invalid_quantity',
          itemCode: dish.code,
          itemName: dish.name,
          ingredientName: ingredient.name,
          message: `Định lượng ${ingredient.name} của ${dish.name} không phải số.`,
        });
        return [];
      }

      return [{
        code: material.cukcukMaterialCode,
        name: material.name,
        unit: material.cukcukUnit,
        quantity: quantity.value,
      }];
    });

    // CUKCUK allows a material code only once per recipe. Legacy 8am
    // formulas can list the same material more than once (for example Xoài
    // in two preparation steps), so combine those lines before exporting.
    const ingredientsByCode = new Map<string, (typeof ingredientCandidates)[number]>();
    ingredientCandidates.forEach((candidate) => {
      const key = normalizeCodeKey(candidate.code);
      const existing = ingredientsByCode.get(key);
      if (existing) {
        existing.quantity += candidate.quantity;
      } else {
        ingredientsByCode.set(key, { ...candidate });
      }
    });
    const ingredients = Array.from(ingredientsByCode.values());

    if (recipe?.optionAddons?.length) {
      warnings.push({
        type: 'option_recipe_not_exported',
        itemCode: dish.code,
        itemName: dish.name,
        message: `Công thức của sở thích phục vụ trong ${dish.name} chưa thể đưa vào cột định lượng; chỉ giữ lại danh sách STPV.`,
      });
    }

    const options = input.includeServicePreferences === true
      ? optionRowsForDish(dish.code, input.customizations || [])
      : [];
    // A menu row without at least one importable ingredient is rejected by
    // CUKCUK with "Không nhập định lượng NVL". This export is for recipes,
    // so leave those dishes out and keep the warning for the review dialog.
    if (ingredients.length === 0 && options.length === 0) return;
    const rowCount = Math.max(1, ingredients.length, options.length);
    for (let index = 0; index < rowCount; index += 1) {
      const row = index === 0 ? baseDishRow(dish) : Array(MENU_COLUMN_COUNT).fill('');
      const ingredient = ingredients[index];
      if (ingredient) {
        row[18] = ingredient.code;
        row[19] = ingredient.name;
        row[20] = ingredient.unit;
        row[21] = ingredient.quantity;
        exportedIngredientRows += 1;
      }
      const option = options[index];
      if (option) {
        row[22] = option.label;
        row[23] = option.extraPrice;
      }
      dataRows.push(row);
    }
  });

  const workbook = XLSX.utils.book_new();
  const menuRows = [...instructionsRows(), sectionRow(), headerRow(), subHeaderRow(), ...dataRows];
  XLSX.utils.book_append_sheet(workbook, makeSheet(menuRows), 'Thực đơn');
  XLSX.utils.book_append_sheet(workbook, buildExampleSheet(), 'Ví dụ mẫu');

  return {
    workbook,
    summary: {
      exportedDishCount: dishes.length,
      matchedRecipeCount: matchedRecipeIds.size,
      exportedIngredientRows,
      warnings,
    },
  };
};
